import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Componente de rota protegida
 * Controla acesso baseado em autenticação e nível de permissão
 */
const ProtectedRoute = ({
  children,
  requiredLevel = null,
  allowedLevels = null,
  redirectTo = '/login'
}) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  // Hierarquia de níveis de acesso (Sincronizado com AuthContext)
  const accessLevels = {
    'user': 0,
    'criador_de_conteudo': 1,
    'suporte': 1,
    'suporte_compras': 1,
    'suporte_descontos': 1,
    'moderador': 1,
    'super_moderador': 1,
    'programador': 1,
    'head_suporte': 2,
    'head_moderacao': 2,
    'head_staff': 3,
    'analista_qualidade': 3,
    'departamento_juridico': 3,
    'direcao': 4,
    'admin': 5
  };

  // Mostra loading enquanto verifica autenticação
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mb-4">
            <Loader2 className="h-8 w-8 text-white animate-spin" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">
            Verificando acesso...
          </h2>
          <p className="text-gray-400">
            Aguarde um momento
          </p>
        </motion.div>
      </div>
    );
  }

  // Redireciona para login se não autenticado
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Verifica nível de acesso específico
  if (requiredLevel) {
    const userLevel = accessLevels[user.accessLevel] || 0;
    const requiredLevelValue = accessLevels[requiredLevel] || 0;

    if (userLevel < requiredLevelValue) {
      return <AccessDenied userLevel={user.accessLevel} requiredLevel={requiredLevel} />;
    }
  }

  // Verifica se o usuário está na lista de níveis permitidos
  if (allowedLevels && Array.isArray(allowedLevels)) {
    if (!allowedLevels.includes(user.accessLevel)) {
      return <AccessDenied userLevel={user.accessLevel} allowedLevels={allowedLevels} />;
    }
  }

  // Renderiza o componente filho se todas as verificações passaram
  return children;
};

/**
 * Componente de acesso negado
 * Exibido quando usuário não tem permissão suficiente
 */
const AccessDenied = ({ userLevel, requiredLevel, allowedLevels }) => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate('/');
  };

  const getLevelName = (level) => {
    const levelNames = {
      'user': 'Usuário',
      'support': 'Suporte',
      'moderator': 'Moderador',
      'programmer': 'Programador',
      'admin': 'Administrador'
    };
    return levelNames[level] || level;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md text-center"
      >
        <div className="bg-black/20 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
          <div className="w-20 h-20 mx-auto bg-gradient-to-br from-red-500 to-orange-500 rounded-2xl flex items-center justify-center mb-6">
            <Shield className="h-10 w-10 text-white" />
          </div>

          <h1 className="text-2xl font-bold text-white mb-4">
            Acesso Negado
          </h1>

          <div className="text-gray-400 mb-6 space-y-2">
            <p>Você não tem permissão para acessar esta área.</p>
            <div className="text-sm">
              <p><strong>Seu nível:</strong> {getLevelName(userLevel)}</p>
              {requiredLevel && (
                <p><strong>Nível necessário:</strong> {getLevelName(requiredLevel)}</p>
              )}
              {allowedLevels && (
                <p><strong>Níveis permitidos:</strong> {allowedLevels.map(getLevelName).join(', ')}</p>
              )}
            </div>
          </div>

          <Button
            onClick={handleGoBack}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-medium py-3 rounded-xl transition-all duration-200"
          >
            Voltar ao Dashboard
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

/**
 * Hook para verificar permissões
 * Útil para mostrar/esconder elementos baseado no nível de acesso
 */
export const usePermissions = () => {
  const { user } = useAuth();

  const accessLevels = {
    'user': 1,
    'criador_de_conteudo': 2,
    'suporte': 2,
    'suporte_compras': 2,
    'suporte_descontos': 2,
    'moderador': 2,
    'super_moderador': 2,
    'programador': 2,
    'head_suporte': 3,
    'head_moderacao': 3,
    'head_staff': 4,
    'analista_qualidade': 4,
    'departamento_juridico': 4,
    'direcao': 5,
    'admin': 6
  };

  const hasLevel = (requiredLevel) => {
    if (!user) return false;
    const userLevel = accessLevels[user.accessLevel] || 0;
    const requiredLevelValue = accessLevels[requiredLevel] || 0;
    return userLevel >= requiredLevelValue;
  };

  const hasAnyLevel = (allowedLevels) => {
    if (!user || !Array.isArray(allowedLevels)) return false;
    return allowedLevels.includes(user.accessLevel);
  };

  const isExactLevel = (level) => {
    if (!user) return false;
    return user.accessLevel === level;
  };

  return {
    hasLevel,
    hasAnyLevel,
    isExactLevel,
    userLevel: user?.accessLevel,
    isUser: user?.accessLevel === 'user',
    isSupport: ['suporte', 'suporte_compras', 'suporte_descontos', 'head_suporte'].includes(user?.accessLevel),
    isModerator: ['moderador', 'super_moderador', 'head_moderacao'].includes(user?.accessLevel),
    isProgrammer: user?.accessLevel === 'programador',
    isAdmin: user?.accessLevel === 'admin',
    isStaff: user?.accessLevel !== 'user' && user?.accessLevel !== 'guest'
  };
};

export default ProtectedRoute;
